import { useCallback, useEffect, useState } from "react";
import { Section, Field, StatusPill } from "@/components/Shell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Loader2, KeyRound, Power, PowerOff, Pencil, Trash2, X, Shield, User as UserIcon, Building2,
} from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  project_manager: "Project Manager",
  company_admin: "Company Admin",
};

const initialForm = {
  email: "",
  name: "",
  password: "",
  role: "company_admin",
  intake_id: "",
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null); // {id, name, role, intake_id, active}
  const [resetting, setResetting] = useState(null); // {id, email}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: us }, { data: it }] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/intakes"),
      ]);
      setUsers(us);
      setIntakes(it);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.email.trim() || !form.name.trim() || !form.password) {
      return toast.error("Email, name and password are required");
    }
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.role === "company_admin" && !form.intake_id) {
      return toast.error("Pick a client for the Company Admin");
    }
    setCreating(true);
    try {
      await api.post("/admin/users", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
        intake_id: form.role === "company_admin" ? form.intake_id : null,
      });
      toast.success(`User ${form.email} created`);
      setForm(initialForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        name: editing.name,
        role: editing.role,
        intake_id: editing.role === "company_admin" ? editing.intake_id : null,
        active: editing.active,
      });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (u) => {
    if (u.id === me.id && u.active) {
      return toast.error("You cannot deactivate your own account");
    }
    try {
      await api.patch(`/admin/users/${u.id}`, {
        active: !u.active,
      });
      toast.success(`User ${!u.active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doReset = async (newPw) => {
    try {
      await api.post(`/admin/users/${resetting.id}/reset-password`, { password: newPw });
      toast.success(`Password reset for ${resetting.email}`);
      setResetting(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (u.id === me.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Section kicker="Access control" title="Users & permissions">
      {/* Create form */}
      <div className="brut-card p-5 mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
          <Plus size={12} className="inline -mt-0.5 mr-1" />
          Invite a new user
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Name" required>
            <input
              className="input-brut"
              data-testid="users-new-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Jane Doe"
            />
          </Field>
          <Field label="Email" required>
            <input
              type="email"
              className="input-brut"
              data-testid="users-new-email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="jane@company.com"
            />
          </Field>
          <Field label="Temporary password" required>
            <input
              type="text"
              className="input-brut"
              data-testid="users-new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="min. 6 chars"
            />
          </Field>
          <Field label="Role" required>
            <select
              className="select-brut"
              data-testid="users-new-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            >
              <option value="super_admin">Super Admin</option>
              <option value="project_manager">Project Manager</option>
              <option value="company_admin">Company Admin</option>
            </select>
          </Field>
          {form.role === "company_admin" && (
            <Field label="Client" required>
              <select
                className="select-brut"
                data-testid="users-new-intake"
                value={form.intake_id}
                onChange={(e) => setForm((f) => ({ ...f, intake_id: e.target.value }))}
              >
                <option value="">Select client…</option>
                {intakes.filter((it) => it.active !== false).map((it) => (
                  <option key={it.id} value={it.id}>{it.client_name}</option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <div className="mt-4">
          <button
            className="btn-brut btn-flame"
            data-testid="users-create-btn"
            onClick={create}
            disabled={creating}
          >
            {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
            Create user
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-black/60">Loading users…</div>
      ) : (
        <div className="brut-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[rgb(var(--ink))] text-[rgb(var(--paper))]">
              <tr>
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Scope</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-t-2 border-black hover:bg-[rgb(var(--paper-2))]"
                >
                  <Td>
                    <div className="font-medium">{u.name}</div>
                    <div className="font-mono text-xs text-black/60">{u.email}</div>
                  </Td>
                  <Td>
                    <span className={`pill ${
                      u.role === "super_admin" ? "pill-flame"
                      : u.role === "company_admin" ? "pill-teal"
                      : "pill-ink"
                    }`}>
                      {u.role === "super_admin" && <Shield size={10} />}
                      {u.role === "company_admin" && <Building2 size={10} />}
                      {u.role === "project_manager" && <UserIcon size={10} />}
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </Td>
                  <Td>
                    {u.intake_client_name ? (
                      <span className="text-sm">{u.intake_client_name}</span>
                    ) : (
                      <span className="font-mono text-xs text-black/50">All clients</span>
                    )}
                  </Td>
                  <Td>
                    <StatusPill status={u.active ? "verified" : "on_hold"} />
                    {u.id === me.id && (
                      <span className="ml-2 pill pill-muted text-[10px]">you</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap justify-end">
                      <button
                        className="btn-brut btn-ghost text-xs"
                        data-testid={`user-edit-${u.id}`}
                        onClick={() => setEditing({ ...u })}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        className="btn-brut btn-ghost text-xs"
                        data-testid={`user-reset-${u.id}`}
                        onClick={() => setResetting({ id: u.id, email: u.email })}
                      >
                        <KeyRound size={12} /> Reset
                      </button>
                      <button
                        className="btn-brut btn-ghost text-xs"
                        data-testid={`user-toggle-${u.id}`}
                        onClick={() => toggleActive(u)}
                      >
                        {u.active ? <PowerOff size={12} /> : <Power size={12} />}
                      </button>
                      {u.id !== me.id && (
                        <button
                          className="btn-brut btn-ghost text-xs"
                          data-testid={`user-delete-${u.id}`}
                          onClick={() => remove(u)}
                          title="Delete user"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          intakes={intakes}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          isSelf={editing.id === me.id}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          email={resetting.email}
          onCancel={() => setResetting(null)}
          onConfirm={doReset}
        />
      )}
    </Section>
  );
}

function EditUserModal({ user, intakes, onChange, onCancel, onSave, isSelf }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4"
      onClick={onCancel}
    >
      <div
        className="brut-card bg-[rgb(var(--paper))] max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-xs uppercase tracking-widest">
            Edit user · {user.email}
          </div>
          <button className="btn-brut btn-ghost text-xs" onClick={onCancel}>
            <X size={12} />
          </button>
        </div>
        <div className="space-y-4">
          <Field label="Name" required>
            <input
              className="input-brut"
              value={user.name}
              onChange={(e) => onChange({ ...user, name: e.target.value })}
            />
          </Field>
          <Field label="Role" required>
            <select
              className="select-brut"
              value={user.role}
              onChange={(e) =>
                onChange({
                  ...user,
                  role: e.target.value,
                  intake_id: e.target.value === "company_admin" ? user.intake_id : null,
                })
              }
              disabled={isSelf}
            >
              <option value="super_admin">Super Admin</option>
              <option value="project_manager">Project Manager</option>
              <option value="company_admin">Company Admin</option>
            </select>
            {isSelf && (
              <div className="text-[10px] font-mono text-black/60 mt-1">
                You cannot change your own role.
              </div>
            )}
          </Field>
          {user.role === "company_admin" && (
            <Field label="Client" required>
              <select
                className="select-brut"
                value={user.intake_id || ""}
                onChange={(e) => onChange({ ...user, intake_id: e.target.value })}
              >
                <option value="">Select client…</option>
                {intakes.filter((it) => it.active !== false).map((it) => (
                  <option key={it.id} value={it.id}>{it.client_name}</option>
                ))}
              </select>
            </Field>
          )}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 border-2 border-black accent-black"
              checked={user.active}
              onChange={(e) => onChange({ ...user, active: e.target.checked })}
              disabled={isSelf}
            />
            <span className="text-sm">Account active</span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button className="btn-brut btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn-brut btn-flame" onClick={onSave} data-testid="user-edit-save">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ email, onCancel, onConfirm }) {
  const [pw, setPw] = useState("");
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4"
      onClick={onCancel}
    >
      <div
        className="brut-card bg-[rgb(var(--paper))] max-w-sm w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-xs uppercase tracking-widest">
            Reset password
          </div>
          <button className="btn-brut btn-ghost text-xs" onClick={onCancel}>
            <X size={12} />
          </button>
        </div>
        <div className="text-sm mb-3">
          Set a new password for <b className="font-mono">{email}</b>.
        </div>
        <Field label="New password" required>
          <input
            type="text"
            className="input-brut"
            data-testid="user-reset-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="min. 6 chars"
          />
        </Field>
        <div className="mt-5 flex justify-end gap-3">
          <button className="btn-brut btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            className="btn-brut btn-flame"
            data-testid="user-reset-confirm"
            onClick={() => {
              if (pw.length < 6) return toast.error("Password must be at least 6 characters");
              onConfirm(pw);
            }}
          >
            <KeyRound size={14} /> Set password
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em]">
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td className="px-4 py-3 align-middle">{children}</td>;
}
